/*
 * MigrationExportSummary.tsx (RSX)
 *
 * Copyright © 2026 RSX Technologies GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all it's contents are proprietary and confidential.
 *
 * @file MigrationExportSummary.tsx
 * @copyright 2026 RSX Technologies GmbH. All rights reserved.
 * @section License
 */

import React, { useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { Fieldset } from 'primereact/fieldset';
import { useTranslation } from 'react-i18next';
import Loader from '@abstract/abstractwebcommon-client/Loader';
import ConfirmationPopup from '@abstract/abstractwebcommon-client/ConfirmationPopup';
import { formatDate } from '@abstract/abstractwebcommon-shared/utils/sharedFunctions';
import {
  IMigrationExportSummary,
  MigrationExportRecordKind
} from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/MigrationExport';

/**
 * Interface for MigrationExportSummary properties.
 */
interface IMigrationExportSummaryProperties {
  summary: IMigrationExportSummary | null;
  isLoading: boolean;
  isPreparingDownload: boolean;
  startDownload: () => void;
  startDocumentsDownload: () => void;
}

/**
 * The record kinds in the order the file writes them, so the table reads as the export reads.
 * @constant
 */
const RECORD_KINDS: MigrationExportRecordKind[] = [
  MigrationExportRecordKind.application,
  MigrationExportRecordKind.product,
  MigrationExportRecordKind.taxRate,
  MigrationExportRecordKind.discount,
  MigrationExportRecordKind.customer,
  MigrationExportRecordKind.userCache,
  MigrationExportRecordKind.subscription,
  MigrationExportRecordKind.transaction
];

/**
 * The drain counts, in the order they have to be dealt with.
 *
 * Each is work that can only be done **in this store**, and none of it can be carried across, so
 * the screen states every one of them whether or not it is zero. A list that hid the satisfied
 * rows would leave an operator unable to tell "there are none of these" from "this store does not
 * check for that" — which on a queue holding a decision somebody owes a customer is the difference
 * between finished and forgotten.
 * @constant
 */
const DRAIN_KEYS: string[] = [
  'pendingRefundRequests',
  'pendingSeatUpdates',
  'unfulfilledTransactions',
  'paidSubscriptionsWithoutBraintreeID',
  'transactionsWithoutUserUUID',
  'manualTransactionsWithoutUserUUID',
  'customersWithoutUserUUID'
];

const MigrationExportSummary = (
  properties: IMigrationExportSummaryProperties
) => {
  const translation = useTranslation().t;
  const [
    isDownloadConfirmationVisible,
    setDownloadConfirmationVisible
  ] = useState<boolean>(false);
  const [confirmPopupTarget, setConfirmPopupTarget] = useState<any>(null);
  const [
    isDocumentsConfirmationVisible,
    setDocumentsConfirmationVisible
  ] = useState<boolean>(false);
  const [documentsConfirmTarget, setDocumentsConfirmTarget] = useState<any>(
    null
  );

  if (properties.isLoading && !properties.summary) {
    return <Loader />;
  }

  const summary: IMigrationExportSummary | null = properties.summary;

  if (!summary) {
    return (
      <Alert variant="danger">
        {translation('admin.migrationExport.unavailable')}
      </Alert>
    );
  }

  /// How many drain rows are non-zero. Stated rather than implied, because the button stays live.
  const outstanding: number = DRAIN_KEYS.filter(
    (key: string) => Number((summary.drain as any)[key] ?? 0) > 0
  ).length;

  const totalRecords: number = RECORD_KINDS.reduce(
    (sum: number, kind: MigrationExportRecordKind) =>
      sum + Number(summary.counts?.[kind] ?? 0),
    0
  );

  return (
    <Row>
      <Col sm={12}>
        <Alert variant="info">
          {translation('admin.migrationExport.explanation')}
        </Alert>
      </Col>

      <Col sm={12} md={6}>
        <Fieldset legend={translation('admin.migrationExport.source.legend')}>
          <dl className="row mb-0">
            <dt className="col-sm-6">
              {translation('admin.migrationExport.source.store')}
            </dt>
            <dd className="col-sm-6">{summary.source.store}</dd>

            <dt className="col-sm-6">
              {translation('admin.migrationExport.source.environment')}
            </dt>
            <dd className="col-sm-6">
              {summary.source.braintreeEnvironment ?? '—'}
            </dd>

            <dt className="col-sm-6">
              {translation('admin.migrationExport.source.merchant')}
            </dt>
            <dd className="col-sm-6">
              {/* NOTE: The importer refuses a batch whose merchant does not match its own
                  NOTE: Braintree provider, so this is the one field worth checking by eye before
                  NOTE: the file is carried anywhere. */}
              {summary.source.braintreeMerchantID || '—'}
            </dd>

            <dt className="col-sm-6">
              {translation('admin.migrationExport.source.merchantAccount')}
            </dt>
            <dd className="col-sm-6">
              {summary.source.braintreeMerchantAccountID ?? '—'}
            </dd>

            <dt className="col-sm-6">
              {translation('admin.migrationExport.source.range')}
            </dt>
            <dd className="col-sm-6">
              {/* NOTE: Both dates are null on a store that has never sold anything, and
                  NOTE: `formatDate` takes a string — so the range is rendered only when both
                  NOTE: halves exist rather than printing a dash against a real date. */}
              {summary.earliestTransactionDate && summary.latestTransactionDate
                ? `${formatDate(
                    summary.earliestTransactionDate
                  )} — ${formatDate(summary.latestTransactionDate)}`
                : '—'}
            </dd>

            <dt className="col-sm-6">
              {translation('admin.migrationExport.source.plans')}
            </dt>
            <dd className="col-sm-6">
              {summary.braintreePlanIDs.length
                ? summary.braintreePlanIDs.join(', ')
                : '—'}
            </dd>
          </dl>
        </Fieldset>
      </Col>

      <Col sm={12} md={6}>
        <Fieldset legend={translation('admin.migrationExport.contents.legend')}>
          <table className="table table-sm mb-0">
            <tbody>
              {RECORD_KINDS.map((kind: MigrationExportRecordKind) => (
                <tr key={kind}>
                  <td>
                    {translation(`admin.migrationExport.kind.${kind}`)}
                  </td>
                  <td className="text-right">
                    {Number(summary.counts?.[kind] ?? 0).toLocaleString()}
                  </td>
                </tr>
              ))}
              <tr>
                <th>{translation('admin.migrationExport.contents.total')}</th>
                <th className="text-right">
                  {totalRecords.toLocaleString()}
                </th>
              </tr>
              <tr>
                {/* NOTE: Below the total rather than inside it: these are not records of the
                    NOTE: export above, they are a second download of their own. */}
                <td>
                  {translation('admin.migrationExport.contents.documents')}
                </td>
                <td className="text-right">
                  {Number(summary.invoiceDocuments ?? 0).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </Fieldset>
      </Col>

      <Col sm={12}>
        <Fieldset legend={translation('admin.migrationExport.drain.legend')}>
          <Alert variant={outstanding ? 'warning' : 'success'}>
            {outstanding
              ? translation('admin.migrationExport.drain.outstanding', {
                  count: outstanding
                })
              : translation('admin.migrationExport.drain.clear')}
          </Alert>

          <table className="table table-sm">
            <tbody>
              {DRAIN_KEYS.map((key: string) => {
                const count: number = Number(
                  (summary.drain as any)[key] ?? 0
                );
                return (
                  <tr key={key} className={count ? 'table-warning' : ''}>
                    <td>{translation(`admin.migrationExport.drain.${key}`)}</td>
                    <td className="text-right">{count.toLocaleString()}</td>
                  </tr>
                );
              })}
              <tr
                className={summary.staleUserCacheRows ? 'table-warning' : ''}
              >
                <td>
                  {translation('admin.migrationExport.drain.staleUserCache')}
                </td>
                <td className="text-right">
                  {Number(summary.staleUserCacheRows ?? 0).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </Fieldset>
      </Col>

      <Col sm={12} className="d-flex justify-content-end">
        <Button
          variant="outline-primary"
          className="mr-2"
          disabled={
            properties.isPreparingDownload || !summary.invoiceDocuments
          }
          onClick={(event: any) => {
            setDocumentsConfirmTarget(event.target);
            setDocumentsConfirmationVisible(true);
          }}>
          {translation('admin.migrationExport.download.documentsButton')}
        </Button>
        <ConfirmationPopup
          target={documentsConfirmTarget}
          isShow={isDocumentsConfirmationVisible}
          title={translation('admin.migrationExport.download.confirmDocuments', {
            count: Number(summary.invoiceDocuments ?? 0)
          })}
          onAccept={() => {
            setDocumentsConfirmationVisible(false);
            properties.startDocumentsDownload();
          }}
          onReject={() => setDocumentsConfirmationVisible(false)}
          acceptBtnClass="primary"
          rejectBtnClass="secondary"
          rejectLabel={translation('/confirm_messages.no')}
          acceptLabel={translation('/confirm_messages.yes')}
          acceptBtnIcon="bi bi-check2-circle"
          rejectBtnIcon="bi bi-x-circle"
        />
        <Button
          variant="primary"
          disabled={properties.isPreparingDownload}
          onClick={(event: any) => {
            setConfirmPopupTarget(event.target);
            setDownloadConfirmationVisible(true);
          }}>
          {properties.isPreparingDownload
            ? translation('admin.migrationExport.download.preparing')
            : translation('admin.migrationExport.download.button')}
        </Button>
        <ConfirmationPopup
          target={confirmPopupTarget}
          isShow={isDownloadConfirmationVisible}
          title={
            outstanding
              ? translation(
                  'admin.migrationExport.download.confirmOutstanding',
                  { count: outstanding }
                )
              : translation('admin.migrationExport.download.confirm')
          }
          onAccept={() => {
            setDownloadConfirmationVisible(false);
            properties.startDownload();
          }}
          onReject={() => setDownloadConfirmationVisible(false)}
          acceptBtnClass="primary"
          rejectBtnClass="secondary"
          rejectLabel={translation('/confirm_messages.no')}
          acceptLabel={translation('/confirm_messages.yes')}
          acceptBtnIcon="bi bi-check2-circle"
          rejectBtnIcon="bi bi-x-circle"
        />
      </Col>
    </Row>
  );
};

export default MigrationExportSummary;
